import { useEffect, useRef } from 'react';
import { byteSizeB } from '../more/moreB';
import { useRecovery } from './useRecovery';

/**
 * **B-21 — §13.5's recovery screen.** The one surface that runs when the app cannot open its own
 * database, because the data on the phone was written by a newer build.
 *
 * Design §13.5: *"a dedicated screen offering a **real BAK-1 export** … plus instructions to update,
 * rather than a crash"*, and B spec §9's failure table: *"export + update; never 'repair'"*. The
 * engine has been promising this screen in a user-facing sentence since U-6 — `data/db.ts`'s
 * `SchemaVersionError` reads *"your matches are safe and can be exported from the recovery screen"* —
 * and until this unit that sentence pointed at nothing.
 *
 * ── What a person is actually doing when they see this ──────────────────────
 *
 * They are at a pitch, a match is about to start, and the app they scout with will not open. So the
 * screen answers three questions in the order they are asked, and refuses to be a settings page:
 *
 * 1. **Is my work gone?** — first, before anything else, and the answer is a counted one. *"Nothing
 *    has been changed or deleted"* is a claim; *"27 matches, 1,412 observations, 38 voice notes"* is
 *    the same claim with its evidence, read back out of the database that is supposedly unreachable.
 * 2. **What happened?** — two version numbers side by side. The whole condition is that one is
 *    larger than the other, so showing both, adjacent and equally weighted, IS the explanation;
 *    prose alone would make the reader take it on trust.
 * 3. **What do I do?** — one primary action (get the file off the phone) and one instruction
 *    (update the app). Nothing else is offered, including the thing a user might most want offered:
 *    see `useRecovery`'s note on why there is no repair or reset control.
 *
 * ── Craft notes (mobile-ui-design skill) ────────────────────────────────────
 *
 * A **reading/decision surface**, not a live-logging one: reading typography leads, text selection
 * stays enabled (a support conversation may need these numbers copied), and density can rise. The
 * export control takes the in-play 60 pt floor rather than the 44 pt secondary one even though it is
 * pressed once — sizing is by *role*, and this is the single most consequential button in the app at
 * the moment it is shown. State is never carried by hue alone: the version pair is labelled, the
 * result is a sentence, and the problem line takes a left rail plus its own words.
 */

const MODULE_TITLE = 'Update Touchline to use this data';

/**
 * The counts worth naming, in the user's words rather than the schema's.
 *
 * Deliberately a short list, and deliberately in this order — *matches* first because it is the unit
 * a coach counts their season in. Anything not named here is still in the export and still in
 * `totalRows`; naming every store would turn the reassurance into a schema dump.
 */
const SUMMARY_ROWS: readonly { readonly store: string; readonly one: string; readonly many: string }[] = [
  { store: 'matches', one: 'match', many: 'matches' },
  { store: 'observations', one: 'observation', many: 'observations' },
  { store: 'audioBlobs', one: 'voice note', many: 'voice notes' },
  { store: 'teams', one: 'opponent', many: 'opponents' },
];

function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

export function SchemaRecoveryScreen() {
  const { state, actions } = useRecovery();
  const { overview } = state;

  /**
   * **The outcome of the export is brought into view when it arrives.**
   *
   * Found by reading this unit's own screenshots, which is the only thing that could have found it:
   * the export control sits at the bottom of a screen taller than a 390×844 viewport, so its result
   * line rendered **below the fold**. Every automated check passed — the file downloaded, the text
   * was in the DOM, the assertions read it — while the person who pressed the button saw nothing
   * happen at all. That is this project's most repeated defect shape (*committed, but not rendered*)
   * in its purest form: rendered, but not where anyone can see it.
   *
   * `block: 'nearest'` scrolls the minimum needed and does nothing when the result is already
   * visible, and the default instant behaviour means there is no motion to trade away under
   * `prefers-reduced-motion`.
   */
  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const problemRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    const target = outcomeRef.current ?? problemRef.current;
    target?.scrollIntoView({ block: 'nearest' });
  }, [state.exported, state.exportProblem]);

  if (state.phase === 'loading') {
    return (
      <div className="recovery-b" data-testid="recovery-b" data-phase="loading">
        <p className="recovery-b__loading">Checking what is on this device…</p>
      </div>
    );
  }

  return (
    <div
      className="recovery-b"
      data-testid="recovery-b"
      data-phase={state.phase}
      data-read={String(state.reads)}
      data-stored-version={overview === null ? '' : String(overview.storedVersion)}
      data-bundle-version={overview === null ? '' : String(overview.bundleVersion)}
      data-rows={overview === null ? '' : String(overview.totalRows)}
      data-exported={state.exported === null ? 'no' : 'yes'}
    >
      <h1 className="recovery-b__title">{MODULE_TITLE}</h1>

      {/* ── 1. Is my work gone? ─────────────────────────────────────────────── */}
      <section className="recovery-card" data-testid="recovery-safe" aria-labelledby="rec-safe-h">
        <h2 className="recovery-card__title" id="rec-safe-h">
          Your data is safe
        </h2>
        {/*
          Two sentences, because there are two situations and one of them makes the other a lie.
          The reassurance names what this app CAN still do; if the read that produces the counts
          below failed, it could not do that, and saying so anyway would be a composed-copy defect
          of the worst kind — a false promise about the user's data, on the screen they came to for
          the truth about their data.
        */}
        <p className="recovery-card__lead">
          {state.problem === null
            ? 'Nothing has been changed or deleted. Touchline can still read everything on this phone — it just cannot open it for normal use until the app is updated.'
            : 'Nothing has been changed or deleted — this app has not written to this phone and will not. It could not finish listing what is here, so the counts are missing, but the export below still runs and saves everything it can reach.'}
        </p>
        {overview !== null && (
          <p className="recovery-b__counts" data-testid="recovery-counts">
            <span className="recovery-b__total">{overview.totalRows.toLocaleString()}</span>
            <span className="recovery-b__total-label">items still on this device</span>
          </p>
        )}
        {overview !== null && (
          <ul className="recovery-b__breakdown" data-testid="recovery-breakdown">
            {SUMMARY_ROWS.filter((row) => (overview.rowCounts[row.store] ?? 0) > 0).map((row) => (
              <li key={row.store}>{plural(overview.rowCounts[row.store] ?? 0, row.one, row.many)}</li>
            ))}
          </ul>
        )}
        {state.problem !== null && (
          <p className="recovery-problem" data-testid="recovery-problem" role="alert">
            The data on this device could not be read: {state.problem}
          </p>
        )}
      </section>

      {/* ── 2. What happened? ─────────────────────────────────────────────────
          Rendered even when the counts could not be read. The version gap is WHY this screen is on
          screen at all — `checkSchema` established it before anything here ran — so an explanation
          that disappeared along with the counts would leave the user on an unexplained error page.
          Only the two numbers are conditional; the sentence never is. */}
      <section className="recovery-card" data-testid="recovery-versions" aria-labelledby="rec-ver-h">
        <h2 className="recovery-card__title" id="rec-ver-h">
          What happened
        </h2>
        {overview !== null && (
          <div className="recovery-versions">
            <div className="recovery-version">
              <span className="recovery-version__label">Your data</span>
              <span className="recovery-version__value" data-testid="recovery-stored-version">
                v{overview.storedVersion}
              </span>
            </div>
            <div className="recovery-version recovery-version--behind">
              <span className="recovery-version__label">This app</span>
              <span className="recovery-version__value" data-testid="recovery-bundle-version">
                v{overview.bundleVersion}
              </span>
            </div>
          </div>
        )}
        <p className="recovery-card__lead">
          A newer version of Touchline has written to this phone — from another device, or because
          this copy was reinstalled from an older build. An older app cannot safely read data a
          newer one wrote, so it stops rather than risk changing it.
        </p>
      </section>

      {/* ── 3. What do I do? ────────────────────────────────────────────────── */}
      <section className="recovery-card" data-testid="recovery-export-card" aria-labelledby="rec-exp-h">
        <h2 className="recovery-card__title" id="rec-exp-h">
          Get a copy off this phone
        </h2>
        <p className="recovery-card__lead">
          One file holding everything on this device — every match, pattern, flag, voice note and
          card. Do this first, before anything else. It costs nothing and changes nothing.
        </p>
        <button
          type="button"
          className="recovery-btn recovery-btn--primary"
          data-testid="recovery-export"
          /*
           * **Never disabled by a failed read.** §13.5's whole argument is that *"an export must
           * survive a damaged profile rather than refuse to run on one"* — and `serializeArchive`
           * is built for exactly that, reporting row-level damage instead of throwing. A profile
           * whose counts could not be listed is the case that argument is about, so disabling the
           * button there would use the one screen that exists to rescue damaged data to refuse to
           * rescue damaged data. Busy is the only reason to be inert.
           */
          disabled={state.busy}
          onClick={() => {
            actions.runExport(Date.now());
          }}
        >
          {state.busy ? 'Preparing your file…' : 'Export everything'}
        </button>
        {state.exported !== null && (
          <div
            className="recovery-notice"
            data-testid="recovery-export-result"
            role="status"
            ref={outcomeRef}
          >
            <p>
              {state.exported.shared ? 'Shared' : 'Saved'}{' '}
              <strong>{state.exported.filename}</strong> · {byteSizeB(state.exported.bytes)} ·{' '}
              {plural(state.exported.blobs, 'voice note', 'voice notes')} included.
            </p>
            {state.exported.rescuedTables.length > 0 && (
              <p data-testid="recovery-export-rescued">
                Data from the newer version travelled too, in <code>recovery/rescue.json</code>:{' '}
                {state.exported.rescuedTables.join(', ')}.
              </p>
            )}
            {state.exported.unreadableStores.length > 0 && (
              <p data-testid="recovery-export-unreadable">
                <strong>Not everything could be read.</strong> These parts of the database would not
                open, so they are not in the file: {state.exported.unreadableStores.join(', ')}.
                Everything else is.
              </p>
            )}
            {state.exported.withheldSettingKeys.length > 0 && (
              <p data-testid="recovery-export-withheld">
                Left out on purpose, because no Touchline archive carries a setting this app cannot
                vouch for: {state.exported.withheldSettingKeys.join(', ')}. You can read them in the
                newer Touchline.
              </p>
            )}
          </div>
        )}
        {state.exportProblem !== null && (
          <p
            className="recovery-problem"
            data-testid="recovery-export-problem"
            role="status"
            ref={problemRef}
          >
            {state.exportProblem}
          </p>
        )}
      </section>

      <section className="recovery-card" data-testid="recovery-next" aria-labelledby="rec-next-h">
        <h2 className="recovery-card__title" id="rec-next-h">
          Then update the app
        </h2>
        <p className="recovery-card__lead">
          Open Touchline on the device that has the newer version and let it update, or reinstall
          this one from the current address. Everything on this phone will be there again — the
          update reads it as it is.
        </p>
        {/*
          §13.5 states this caveat in as many words: "an older client cannot import an archive
          containing newer-schema rows". It is on the screen rather than in a doc because the
          alternative is a user importing this file into THIS copy, watching it fail, and concluding
          the file is broken.
        */}
        <p className="recovery-card__note" data-testid="recovery-caveat">
          The file you just exported opens in the newer Touchline. This older copy cannot read it
          back — that is the same version gap, in the other direction.
        </p>
      </section>
    </div>
  );
}
